import React, { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useVault } from "@/contexts/VaultContext";
import { toast } from "sonner";
import MasterAdminDialog from "@/components/MasterAdminDialog";
import {
  Users, Sparkles, FileText, LogOut, Settings as SettingsIcon, BarChart3,
  ShieldCheck, Globe2, Facebook, Linkedin, UserCheck, Clock, Mail, Search,
  MessageSquare, Phone, Bot, Brain, Headphones, DollarSign,
  Receipt, Wallet, Lock, Unlock, KeyRound, ShieldAlert, Scale, Landmark,
  Files, LayoutGrid, CalendarDays, Film, Send, Gauge, Radar, LayoutDashboard,
  BadgeDollarSign, PanelsTopLeft, Layers, ShoppingCart, TrendingUp, Server,
  FileInput, PiggyBank, Coins, CalendarClock, ClipboardList, Banknote,
  FolderKanban, ListChecks, LifeBuoy, Repeat, BookOpenCheck,
  ChevronDown, ChevronRight, X,
} from "lucide-react";

const adminNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { section: "Work" },
  { to: "/sales-pipeline", label: "Sales Pipeline", icon: TrendingUp, testid: "nav-sales-pipeline" },
  { to: "/proposals", label: "Proposals", icon: FileText, testid: "nav-proposals" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
  { to: "/tasks", label: "Task Manager", icon: ListChecks, testid: "nav-tasks" },
  { to: "/tickets", label: "Tickets", icon: LifeBuoy, testid: "nav-tickets" },
  { to: "/work-logs", label: "Work Logs", icon: ClipboardList, testid: "nav-work-logs" },
  { section: "Platform" },
  { to: "/domains", label: "Domain Names", icon: Globe2, testid: "nav-domains" },
  { to: "/host-servers", label: "Host Servers", icon: Server, testid: "nav-host-servers" },
  { to: "/wordpress", label: "WordPress API", icon: PanelsTopLeft, testid: "nav-wordpress" },
  { to: "/wp-beacon", label: "WP Beacon Dashboard", icon: Radar, testid: "nav-wp-beacon", indent: true },
  { to: "/breakdance", label: "Breakdance API", icon: Layers, testid: "nav-breakdance" },
  { to: "/woocommerce", label: "WooCommerce API", icon: ShoppingCart, testid: "nav-woocommerce" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-master-calendar" },
  { to: "/auto-post-queue", label: "Auto-post queue", icon: Send, testid: "nav-auto-post-queue" },
  { to: "/templates", label: "Templates", icon: Files, testid: "nav-templates" },
  { to: "/team-members", label: "Ops Team", icon: UserCheck, testid: "nav-team-members" },
  { to: "/admin-team", label: "Admin Team", icon: ShieldCheck, testid: "nav-admin-team" },
  { to: "/ai-prompts", label: "AI Prompts", icon: Sparkles, testid: "nav-master-prompts" },
  { to: "/admin/strategy-playbook", label: "Strategy Playbook", icon: BookOpenCheck, testid: "nav-strategy-playbook" },
  { to: "/integrations/google", label: "Google admin", icon: Globe2, testid: "nav-google-integrations" },
  { to: "/integrations/microsoft", label: "Microsoft admin", icon: LayoutGrid, testid: "nav-microsoft-integrations" },
  { to: "/integrations/meta", label: "Meta admin", icon: Facebook, testid: "nav-meta-integrations" },
  { to: "/integrations/linkedin", label: "LinkedIn admin", icon: Linkedin, testid: "nav-linkedin-integrations" },
  { to: "/integrations/sendgrid", label: "SendGrid", icon: Mail, testid: "nav-sendgrid" },
  { to: "/integrations/ahrefs", label: "AHREFS · SEO", icon: Search, testid: "nav-ahrefs" },
  { to: "/integrations/semrush", label: "SEMRUSH · SEO", icon: TrendingUp, testid: "nav-semrush" },
  { to: "/integrations/psi", label: "Google PSI · Speed", icon: Gauge, testid: "nav-psi" },
  { to: "/integrations/woorank", label: "Woorank · SEO Review", icon: Radar, testid: "nav-woorank" },
  { to: "/integrations/wordfence", label: "Wordfence · Security", icon: ShieldAlert, testid: "nav-wordfence" },
  { to: "/integrations/seedance", label: "Seedance · Video AI", icon: Film, testid: "nav-seedance" },
  { to: "/integrations/twilio", label: "Twilio API", icon: MessageSquare, testid: "nav-twilio" },
  { to: "/integrations/3cx", label: "3CX Phone API", icon: Phone, testid: "nav-3cx" },
  { to: "/integrations/3cx/call-centre", label: "3CX Call Centre", icon: Headphones, testid: "nav-3cx-call-centre" },
  { to: "/integrations/voice-agents", label: "AI Voice Agents", icon: Bot, testid: "nav-voice-agents" },
  { to: "/ai-phone-agents", label: "AI Phone Agents", icon: Brain, testid: "nav-ai-phone-agents" },
  { to: "/ai-usage", label: "AI Costs", icon: DollarSign, testid: "nav-ai-usage" },
  { to: "/service-usage", label: "API Usage", icon: Gauge, testid: "nav-api-usage" },
  { section: "Legal · Finance · HR", vaulted: true },
  { to: "/legal-docs", label: "Legal Docs — SLA · T&C", icon: FileText, testid: "nav-legal-docs", vaulted: true },
  { to: "/invoicing", label: "Invoicing - Receivables", icon: BadgeDollarSign, testid: "nav-invoicing", vaulted: true },
  { to: "/invoicing/recurring", label: "Recurring Invoices", icon: Repeat, testid: "nav-recurring-invoices", vaulted: true, indent: true },
  { to: "/bills", label: "Bills - Payables", icon: FileInput, testid: "nav-bills", vaulted: true },
  { to: "/bills/recurring", label: "Recurring Bills", icon: Repeat, testid: "nav-recurring-bills", vaulted: true, indent: true },
  { to: "/merchant-settings", label: "Merchant Settings", icon: KeyRound, testid: "nav-merchant-settings", vaulted: true },
  { to: "/payments", label: "Payments", icon: Wallet, testid: "nav-payments", vaulted: true },
  { to: "/payments/paypal", label: "PayPal", icon: Wallet, testid: "nav-paypal", vaulted: true, indent: true },
  { to: "/integrations/xero", label: "Xero (master)", icon: Receipt, testid: "nav-xero", vaulted: true },
  { to: "/integrations/keypay", label: "KeyPay (Employment Hero)", icon: Wallet, testid: "nav-keypay", vaulted: true },
  { to: "/entitlement-holdings", label: "Entitlement Holdings", icon: PiggyBank, testid: "nav-entitlement-holdings", vaulted: true },
  { to: "/fairwork", label: "Fair Work Compliance", icon: Scale, testid: "nav-fairwork", vaulted: true },
  { to: "/integrations/ato", label: "ATO Access", icon: Landmark, testid: "nav-ato-access", vaulted: true },
  { to: "/tax-holdings", label: "Tax Holdings", icon: Coins, testid: "nav-tax-holdings", vaulted: true },
  { to: "/cashflow-forecast", label: "Cashflow Forecast", icon: CalendarClock, testid: "nav-cashflow-forecast", vaulted: true },
  { to: "/bank-rec", label: "Bank Rec Reports", icon: Banknote, testid: "nav-bank-rec", vaulted: true },
  { to: "/domain-costs", label: "Domain Costs", icon: Globe2, testid: "nav-domain-costs", vaulted: true },
  { section: "System" },
  { to: "/security-stats", label: "Security Stats", icon: ShieldCheck, testid: "nav-security-stats" },
  { to: "/ip-api", label: "IP API", icon: Radar, testid: "nav-ip-api" },
  { to: "/moz-api", label: "MOZ API", icon: BarChart3, testid: "nav-moz-api" },
  { to: "/scheduler", label: "Scheduler", icon: Clock, testid: "nav-scheduler" },
  { to: "/site-meta", label: "Site Meta & SEO", icon: Search, testid: "nav-site-meta" },
  { to: "/ai-access", label: "AI access control", icon: Bot, testid: "nav-ai-access" },
  { to: "/admin/impersonation-log", label: "View-as audit log", icon: ShieldAlert, testid: "nav-impersonation-log" },
  { to: "/vault-settings", label: "Vault password", icon: KeyRound, testid: "nav-vault-settings" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const clientNav = [
  { to: "/portal", label: "Overview", icon: BarChart3, testid: "nav-portal" },
  { to: "/portal/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-portal-calendar" },
  { to: "/portal/charts", label: "Charts", icon: BarChart3, testid: "nav-charts" },
  { to: "/portal/reports", label: "My Reports", icon: FileText, testid: "nav-reports" },
  { to: "/portal/domains", label: "Domains", icon: Globe2, testid: "nav-portal-domains" },
  { to: "/portal/tickets", label: "Tickets", icon: LifeBuoy, testid: "nav-portal-tickets" },
  { to: "/portal/ai", label: "Ask Iceberg AI", icon: Sparkles, testid: "nav-ai" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const vault = useVault();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = user?.role === "admin" ? adminNav : clientNav;
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Group the flat nav config into sections. Items before the first
  // `{section}` marker are "top-of-menu" and always render outside any
  // collapsible block (Dashboard + Clients).
  const grouped = useMemo(() => {
    const top = [];
    const sections = [];
    let current = null;
    for (const n of nav) {
      if (n.section) {
        current = { section: n.section, vaulted: !!n.vaulted, items: [] };
        sections.push(current);
      } else if (!current) {
        top.push(n);
      } else {
        current.items.push(n);
      }
    }
    return { top, sections };
  }, [nav]);

  // Section collapsed state — persisted per user in localStorage so
  // preferences survive reloads. Default = every section collapsed;
  // sections containing the current route auto-open on navigation.
  const storageKey = `sidebar-collapsed-${user?.role || "anon"}`;
  const [openSections, setOpenSections] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return {};
  });
  const toggleSection = (name) => setOpenSections((prev) => {
    const next = { ...prev, [name]: !prev[name] };
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  // Auto-open the section that owns the current route so users never
  // land on a "hidden" active tab.
  const activeSection = useMemo(() => {
    for (const s of grouped.sections) {
      if (s.items.some((it) => it.to && location.pathname.startsWith(it.to))) {
        return s.section;
      }
    }
    return null;
  }, [grouped.sections, location.pathname]);

  // Search filter — case-insensitive, matches label. When active,
  // section headers are hidden and matched items render flat.
  const searchTerm = search.trim().toLowerCase();
  const searchMatches = useMemo(() => {
    if (!searchTerm) return null;
    const all = [...grouped.top,
                 ...grouped.sections.flatMap((s) =>
                   s.items.map((it) => ({ ...it, _section: s.section })))];
    return all.filter((it) => it.label &&
                              it.label.toLowerCase().includes(searchTerm));
  }, [searchTerm, grouped]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const isAdmin = user?.role === "admin";

  return (
    <aside
      className="w-64 shrink-0 border-r border-zinc-200 bg-zinc-50/60 backdrop-blur-sm flex flex-col"
      data-testid="sidebar"
    >
      <div className="px-6 py-6 border-b border-zinc-200 flex items-center gap-3">
        <div className="h-9 w-9 bg-[#002FA7] flex items-center justify-center overflow-hidden">
          <img src="/iceberg-logo.png" alt="Iceberg Digital" className="h-6 w-6 object-contain" />
        </div>
        <div>
          <div className="font-display font-extrabold tracking-tight text-zinc-900 leading-none">Iceberg Digital</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
            {user?.role === "admin" ? "Admin Console" : "Client Portal"}
          </div>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {/* Search — filter menu items by label */}
        <div className="relative mb-2" data-testid="sidebar-search-wrap">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" />
          {/* type="search" + role="search" + a random `name` + off-tags
              keep browser autofill / password managers (1Password,
              LastPass, Chrome-passwords) from silently filling this
              with the currently-signed-in admin email. */}
          <input
            type="search"
            role="searchbox"
            name={`menu-search-${Math.random().toString(36).slice(2, 8)}`}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search menu…"
            className="w-full pl-7 pr-7 py-1.5 text-xs rounded-sm border border-zinc-200 bg-white placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
            data-testid="sidebar-search-input" />
          {search && (
            <button type="button" onClick={() => setSearch("")}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-5 w-5 grid place-items-center text-zinc-400 hover:text-zinc-700"
              data-testid="sidebar-search-clear"
              aria-label="Clear search">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Search results — flat, section headers hidden */}
        {searchMatches ? (
          searchMatches.length === 0 ? (
            <div className="text-[11px] text-zinc-400 italic px-3 py-4 text-center"
              data-testid="sidebar-search-empty">
              No menu items match &quot;{search}&quot;.
            </div>
          ) : (
            searchMatches.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/dashboard" || n.to === "/portal"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150
                   ${isActive
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"}`
                }
              >
                <n.icon className="h-4 w-4" />
                <span className="flex-1 truncate">{n.label}</span>
                {n._section && (
                  <span className="text-[9px] uppercase tracking-wider text-zinc-400 shrink-0">
                    {n._section}
                  </span>
                )}
              </NavLink>
            ))
          )
        ) : (
          <>
            {/* Always-visible top items (Dashboard + Clients) */}
            {grouped.top.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/dashboard" || n.to === "/portal"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150
                   ${isActive
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"}`
                }
              >
                <n.icon className="h-4 w-4" />
                <span className="flex-1">{n.label}</span>
              </NavLink>
            ))}

            {/* Collapsible sections */}
            {grouped.sections.map((s, sidx) => {
              const isOpen = openSections[s.section] ?? (activeSection === s.section);
              const lockBtn = s.vaulted && (vault.unlocked ? (
                <button type="button"
                  onClick={async (e) => {
                    e.stopPropagation();
                    await vault.lock();
                    toast.success("Legal · Finance · HR vault locked");
                  }}
                  title="Click to lock the Legal · Finance · HR section"
                  className="h-4 w-4 grid place-items-center rounded-sm text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 transition"
                  data-testid="nav-vault-unlocked">
                  <Unlock className="h-3 w-3" />
                </button>
              ) : (
                <span className="h-4 w-4 grid place-items-center text-amber-600"
                  title="Section is locked — open Xero or KeyPay to unlock"
                  data-testid="nav-vault-locked">
                  <Lock className="h-3 w-3" />
                </span>
              ));
              const sectionSlug = s.section.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <div key={`sec-${sidx}`} className="pt-3">
                  <button type="button"
                    onClick={() => toggleSection(s.section)}
                    className="w-full flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-semibold hover:text-zinc-700 transition-colors"
                    data-testid={`nav-section-${sectionSlug}`}
                    aria-expanded={isOpen}>
                    {isOpen
                      ? <ChevronDown className="h-3 w-3 shrink-0" />
                      : <ChevronRight className="h-3 w-3 shrink-0" />}
                    <span className="flex-1 text-left">{s.section}</span>
                    {lockBtn}
                  </button>
                  {isOpen && (
                    <div className="space-y-1 mt-1"
                      data-testid={`nav-section-body-${sectionSlug}`}>
                      {s.items.map((n) => {
                        const isPrefixOfAnother = s.items.some(
                          (m) => m.to && m.to !== n.to && m.to.startsWith(n.to + "/"),
                        );
                        return (
                          <NavLink
                            key={n.to}
                            to={n.to}
                            end={isPrefixOfAnother}
                            data-testid={n.testid}
                            className={({ isActive }) =>
                              `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-150
                               ${n.indent ? "pl-8" : ""}
                               ${isActive
                                  ? "bg-zinc-900 text-white"
                                  : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"}`
                            }
                          >
                            <n.icon className={`h-4 w-4 ${n.indent ? "opacity-70" : ""}`} />
                            <span className={`flex-1 ${n.indent ? "text-[13px]" : ""}`}>{n.label}</span>
                            {n.vaulted && !vault.unlocked && (
                              <Lock className="h-3 w-3 text-amber-600 opacity-80" />
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </nav>

      <div className="border-t border-zinc-200 p-4 space-y-3">
        {isAdmin ? (
          <button
            type="button"
            onClick={() => setAdminDialogOpen(true)}
            className="w-full flex items-center gap-3 text-left rounded-sm px-2 py-2 -mx-2 hover:bg-zinc-100 transition-colors group"
            data-testid="sidebar-admin-identity-btn"
            title="Master admin settings — change email, password, and manage YubiKey"
          >
            <div className="h-9 w-9 rounded-full bg-zinc-200 flex items-center justify-center font-display font-bold text-zinc-700 overflow-hidden">
              {user?.google_picture
                ? <img src={user.google_picture} alt="" className="h-full w-full object-cover" />
                : (user?.name?.[0] || "?").toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-zinc-900 truncate flex items-center gap-1"
                data-testid="current-user-name">
                {user?.name}
                <KeyRound className="h-3 w-3 text-zinc-400 group-hover:text-sky-600 shrink-0" />
              </div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            </div>
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-zinc-200 flex items-center justify-center font-display font-bold text-zinc-700 overflow-hidden">
              {user?.google_picture
                ? <img src={user.google_picture} alt="" className="h-full w-full object-cover" />
                : (user?.name?.[0] || "?").toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-zinc-900 truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-300 rounded-sm py-2 transition-colors"
          data-testid="logout-button"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
      {isAdmin && (
        <MasterAdminDialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen} />
      )}
    </aside>
  );
}
